/**
 * H6: exits of the 256 plains (docs/design/2026-10-11-game-skeleton.md). Skeleton: one exit at the horse
 * station at the end of the main road (hs-south) to the road toward town-b. Zone ids are strings only:
 * the greybox town (data/layouts/townB.ts) may not exist; `World.travel` says CLOSED_NOTICE while the
 * zone is not in `ENABLED_ZONES`. Coming back, `arriveFrom` makes the player land beside this same circle.
 */
import type { Warp } from './map';
import type { ZoneId } from './data/zones';

/** Station of the exit, a `HORSE_STATIONS` id. */
export const EXIT_STATION = 'hs-south';
/** The zone beyond (not yet in `ZoneId`). */
export const EXIT_TO = 'town-b';

/** H15: plains-256 exit circles for every enabled zone, by rough direction (one labelled circle each, no travel menu exists). */
export const PLAINS_ROADS: readonly { readonly to: string; readonly side: Warp['side']; readonly at: readonly [number, number]; readonly name: string }[] = [
  // Towns: south, beside the horse station hs-south (b is the station itself).
  { to: 'town-b', side: 's', at: [132.5, 248.5], name: 'ทางไปเหมืองเงาผา' },
  { to: 'town-c', side: 's', at: [112.5, 248.5], name: 'ทางไปท่าระฆังเงียบ' },
  { to: 'town-d', side: 's', at: [152.5, 248.5], name: 'ทางไปเมืองโคมเก้าสาย' },
  // Sects: an arc along the north edge.
  ...['bamboo-tide', 'mist-peak', 'cinnabar', 'pine-ink', 'bell-stone', 'lantern-road', 'dim-moon', 'snow-pine'].map((id, k) => ({
    to: `sect-${id}`, side: 'n' as const, at: [30.5 + k * 28, 4.5] as const, name: `ทางไปสำนัก ${id}`,
  })),
  // Cities: the far edges.
  { to: 'city-trade', side: 'w', at: [3.5, 60.5], name: 'ทางไปเมืองตลาดสายธาร' },
  { to: 'city-sea', side: 'w', at: [3.5, 200.5], name: 'ทางไปท่าคลื่นเงิน' },
  { to: 'city-desert', side: 'e', at: [245.5, 130.5], name: 'ทางไปเมืองทรายแดง' },
  { to: 'city-snow', side: 'n', at: [128.5, 4.5], name: 'ทางไปตำหนักยอดน้ำแข็ง' },
];

/** H15: neighbour links between non-plains zones; the circle stands just inside the wall (x 5.5 west, 90.5 east). */
export const ROAD_LINKS: readonly (readonly [string, string])[] = [
  ['town-b', 'sect-bamboo-tide'],
  ['town-c', 'sect-bell-stone'],
  ['town-d', 'city-trade'],
  ['city-sea', 'town-b'],
  ['sect-snow-pine', 'city-snow'],
];

/** Extra warps of a 96 m shell zone: `[a, b]` leaves a by the east circle, b by its west one (a town can take both). */
export function roadLinkWarps(zone: string): Warp[] {
  const out: Warp[] = [];
  for (const [a, b] of ROAD_LINKS) {
    if (a === zone) out.push({ side: 'e', to: b as ZoneId, x: 90.5, y: 48.5 });
    if (b === zone) out.push({ side: 'w', to: a as ZoneId, x: 5.5, y: 48.5 });
  }
  return out;
}

export function plainsExitWarps(): Warp[] {
  return PLAINS_ROADS.map((r) => ({ side: r.side, to: r.to as ZoneId, x: r.at[0], y: r.at[1] }));
}
